import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Input from '../../../utils/Input';
import Button from '../../../utils/Button';

const ProfileTab: React.FC = () => {
  const [profileData, setProfileData] = useState({
    email: '',
    displayName: '',
    firstName: '',
    lastName: '',
    phone: '',
    bio: '',
    location: '',
    website: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2 text-slate-800">Profile Information</h2>
        <p className="text-gray-400">Manage your personal information and preferences</p>
      </div>
      
      {/* Profile Picture Section */}
      <div className="bg-white rounded-2xl p-8 mb-6 hover:shadow-lg hover:shadow-sky-500/20 transition-all duration-200 text-slate-800">
        <div className="flex items-center space-x-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-sky-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-xl">
              {profileData.displayName ? profileData.displayName.charAt(0).toUpperCase() : 'U'}
            </div>
            <button className="absolute bottom-0 right-0 w-8 h-8 bg-sky-500 rounded-full flex items-center justify-center text-white hover:bg-sky-600 transition-colors shadow-lg">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">Profile Picture</h3>
            <p className="text-gray-400 text-sm mb-3">Upload a new avatar or change your profile picture</p>
            <button className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-medium">
              Change Avatar
            </button>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="bg-white rounded-2xl p-8 hover:shadow-xl hover:shadow-sky-500/20 transition-all duration-300">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Input
              label="Email Address"
              type="email"
              value={profileData.email}
              onChange={handleInputChange('email')}
              placeholder="your@email.com"
              size="large"
              color="white"

            />
          </div>
          <div>
            <Input
              label="Display Name"
              value={profileData.displayName}
              onChange={handleInputChange('displayName')}
              placeholder="John Doe"
              size="large"
              color="white"

            />
          </div>
          <div>
            <Input
              label="First Name"
              value={profileData.firstName}
              onChange={handleInputChange('firstName')}
              placeholder="John"
              size="large"
              color="white"

            />
          </div>
          <div>
            <Input
              label="Last Name"
              value={profileData.lastName}
              onChange={handleInputChange('lastName')}
              placeholder="Doe"
              size="large"
              color="white"

            />
          </div>
          <div>
            <Input
              label="Phone Number"
              value={profileData.phone}
              onChange={handleInputChange('phone')}
              placeholder="+1 (555) 123-4567"
              size="large"
              color="white"

            />
          </div>
          <div>
            <Input
              label="Location"
              value={profileData.location}
              onChange={handleInputChange('location')}
              placeholder="New York, NY"
              size="large"
              color="white"

            />
          </div>
          <div className="md:col-span-2">
            <Input
              className='!bg-white !text-black'
              label="Website"
              value={profileData.website}
              onChange={handleInputChange('website')}
              placeholder="https://example.com"
              size="large"
              color="white"
            />
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-200 mb-2">Bio</label>
          <textarea
            value={profileData.bio}
            onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
            placeholder="Tell us about yourself..."
            rows={4}
            className="w-full bg-white border border-gray-700 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent px-4 py-3 resize-none"
          />
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            label={isSaving ? "Saving..." : "Save Changes"}
            onClick={handleSave}
            disabled={isSaving}
            loading={isSaving}
            variant="primary"
            size="large"
            backgroundColor="linear-gradient(to right, rgb(14 165 233), rgb(147 51 234))"
            additionalClassname="px-6 py-3 hover:from-sky-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
